import { useState, useMemo, useEffect, useRef, ElementRef } from 'react';
import {
  withDatasourceCheck,
  Image,
  ImageField,
  Field,
  ComponentRendering,
} from '@sitecore-jss/sitecore-jss-nextjs';
import useWindowScrollToggle from '../../../hooks/useToggleWindowScroll';
import useHelpers from '../../../hooks/helpers';
import { setCookie } from '../../../hooks/useCookie';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useI18n } from 'next-localization';

import EEPlaceholder from 'components/Sections/EEPlaceholder';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Language = { name: Field<string>; code: Field<string>; countryName: Field<string> };
type Country = {
  name: Field<string>;
  code: Field<string>;
  flag: ImageField;
  languages: Array<Language>;
};
type Region = { name: Field<string>; countries: Array<Country> };
type Fields = {
  title?: Field<string>;
  description?: Field<string>;
  regions?: Array<Region>;
};

type LanguageSelectorProps = {
  fields: Fields;
  rendering: ComponentRendering;
};

const transformLanguages = (language: Language) => {
  return {
    name: language.name.value || '',
    code: language.code.value.toLowerCase() || '',
    countryName: language.countryName.value || '',
  };
};

type TransformedLanguage = ReturnType<typeof transformLanguages>;

const transformCountries = (country: Country) => {
  return {
    name: country.name.value || '',
    code: country.code.value || '',
    flag: country.flag || {},
    languages: country.languages.map(transformLanguages) || [],
  };
};

const transformRegions = (region: Region) => {
  return {
    name: region.name.value || '',
    countries: region.countries.map(transformCountries),
  };
};

const LanguageSelector = ({ fields, rendering }: LanguageSelectorProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const displayLanguage = sitecoreContext.language;
  const { toggleWindowScroll } = useWindowScrollToggle();
  const { anchorId } = useHelpers(rendering);
  const router = useRouter();
  const { t } = useI18n();
  const wrapper = useRef<ElementRef<'div'>>(null);
  const dropdownButton = useRef<ElementRef<'button'>>(null);

  const [isOpen, setIsOpen] = useState(false);
  const title = fields?.title?.value || '';
  const description = fields?.description?.value || '';

  const regions = useMemo(() => {
    return fields.regions?.map(transformRegions) ?? [];
  }, [fields]);

  const selectedCountry = useMemo(() => {
    const allCountries = regions.flatMap((r) => r.countries);
    const selectedCountry = allCountries.find((country) => {
      return country.code.toLowerCase() === displayLanguage?.split('-')?.[1]?.toLowerCase?.();
    });
    return {
      name: selectedCountry?.name,
      flag: selectedCountry?.flag || {},
    };
  }, [regions, displayLanguage]);

  const selectedLanguage = useMemo(() => {
    const allLanguages = regions.flatMap((r) => r.countries.flatMap((c) => c.languages));
    const selectedLanguage = allLanguages.find(
      (language) => language.code.toLowerCase() === displayLanguage?.toLowerCase()
    );
    return {
      name: selectedLanguage?.name,
      code: selectedLanguage?.code.split('-')?.[0]?.toUpperCase?.(),
    };
  }, [regions, displayLanguage]);

  useEffect(() => {
    toggleWindowScroll(isOpen);
  }, [isOpen, toggleWindowScroll]);

  const onFocusOut = () => {
    /*
     * NOTE: `setTimeout` is necessary because `document.activeElement`
     * is not set immediately to the newly focused element after
     * `focusout` event was triggered.
     */
    setTimeout(() => {
      if (!wrapper.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    });
  };

  const onLanguageClick = (language: TransformedLanguage): void => {
    setIsOpen(false);
    if (sitecoreContext.site?.name) {
      setCookie(`${sitecoreContext.site.name.toLowerCase()}#lang`, language.code, 9999);
    }
  };

  if (isExperienceEditor) {
    return <EEPlaceholder>Country selector</EEPlaceholder>;
  } else {
    return (
      <div
        id={anchorId}
        ref={wrapper}
        className="country-selector"
        data-e2e-component-uid={rendering.uid}
        tabIndex={-1}
        onBlur={onFocusOut}
      >
        <button
          ref={dropdownButton}
          className="country-selector__button"
          aria-expanded={isOpen}
          aria-controls="country-selector-menu"
          aria-label={`${t('changeRegion')}, ${selectedCountry.name} ${
            selectedLanguage.name
          } currently selected`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Image
            className="country-selector__flag country-selector__flag--small me-2"
            field={selectedCountry.flag}
            width="50"
            height="32"
          />
          <span className="country-selector__button-text me-1 me-md-2" aria-hidden="true">
            {selectedLanguage.code}
          </span>
          <span className="country-selector__button-text text-decoration-underline me-1">
            {t('changeRegion')}
          </span>
          <i className="bi bi-chevron-down country-selector__icon" />
        </button>

        {isOpen && (
          <section id="country-selector-menu" className="country-selector__menu">
            <div className="py-8 py-md-11">
              <div className="container">
                {title && <h2 className="h1">{title}</h2>}
                {description && <h2 className="body-large mt-5 mt-md-6">{description}</h2>}

                <div
                  className={`country-selector__regions-list ${
                    title || description ? 'mt-7 mt-md-9 mt-lg-11' : ''
                  }`}
                >
                  {regions.map((region) => (
                    <div key={region.name}>
                      <h3 className="h3">{region.name}</h3>

                      <div className="country-selector__countries-list mt-6">
                        {region.countries.map((country) => (
                          <div key={country.name}>
                            <Image
                              className="country-selector__flag country-selector__flag--large"
                              field={country.flag}
                              width="100"
                              height="64"
                            />
                            <div className="country-selector__languages-list mt-2">
                              {country.languages.map((language) => (
                                <Link
                                  key={language.code}
                                  href={router.asPath}
                                  className="text-link text-decoration-underline"
                                  onClick={() => onLanguageClick(language)}
                                  locale={language.code}
                                >
                                  {language.countryName} - {language.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    );
  }
};

export default withDatasourceCheck()<LanguageSelectorProps>(LanguageSelector);
